<template>
  <div class="pdf-thumbnail-sidebar">
    <div class="pdf-thumbnail-sidebar__header">
      Thumbnails
    </div>

    <div class="pdf-thumbnail-sidebar__list">
      <div
        v-for="pageNum in totalPages"
        :key="pageNum"
        class="pdf-thumbnail-sidebar__item"
        :class="{ 'pdf-thumbnail-sidebar__item--active': pageNum === currentPage }"
        @click="emit('select', pageNum)"
      >
        <canvas
          :ref="(el) => setCanvasRef(pageNum, el as HTMLCanvasElement | null)"
          class="pdf-thumbnail-sidebar__canvas"
        />
        <span class="pdf-thumbnail-sidebar__page-number">{{ pageNum }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { watch, onMounted, type PropType } from 'vue'
import type { PDFDocumentProxy } from 'pdfjs-dist'

const THUMBNAIL_SCALE = 0.2

const props = defineProps({
  document: {
    type: Object as PropType<PDFDocumentProxy | null>,
    default: null,
  },
  totalPages: {
    type: Number,
    required: true,
  },
  currentPage: {
    type: Number,
    required: true,
  },
  thumbnailCache: {
    type: Object as PropType<Map<number, ImageBitmap> | null>,
    default: null,
  },
  rotation: {
    type: Number,
    default: 0,
  },
})

const emit = defineEmits<{
  select: [page: number]
}>()

// Canvas refs for thumbnail rendering
const canvasRefs = new Map<number, HTMLCanvasElement>()

function setCanvasRef(pageNum: number, el: HTMLCanvasElement | null): void {
  if (el) {
    canvasRefs.set(pageNum, el)
  } else {
    canvasRefs.delete(pageNum)
  }
}

async function renderThumbnails(): Promise<void> {
  if (!props.document) return

  for (let pageNum = 1; pageNum <= props.totalPages; pageNum++) {
    const canvas = canvasRefs.get(pageNum)
    if (!canvas) continue

    try {
      // Try cache first
      const cached = props.thumbnailCache?.get(pageNum)
      if (cached) {
        canvas.width = cached.width
        canvas.height = cached.height
        const ctx = canvas.getContext('2d')
        if (ctx) {
          ctx.drawImage(cached, 0, 0)
          continue
        }
      }

      // Full render
      const page = await props.document.getPage(pageNum)
      const viewport = page.getViewport({ scale: THUMBNAIL_SCALE, rotation: props.rotation })

      canvas.width = viewport.width
      canvas.height = viewport.height

      const ctx = canvas.getContext('2d')
      if (!ctx) continue

      await page.render({
        canvasContext: ctx,
        viewport,
      }).promise

      // Store in cache
      if (props.thumbnailCache) {
        try {
          const bitmap = await createImageBitmap(canvas)
          props.thumbnailCache.set(pageNum, bitmap)
        } catch {
          // Best-effort
        }
      }
    } catch (error) {
      console.error(`Failed to render thumbnail for page ${pageNum}:`, error)
    }
  }
}

watch(
  () => props.document,
  () => {
    setTimeout(renderThumbnails, 100)
  },
)

watch(
  () => props.rotation,
  () => {
    // Clear thumbnail cache on rotation change and re-render
    if (props.thumbnailCache) {
      for (const bitmap of props.thumbnailCache.values()) {
        bitmap.close()
      }
      props.thumbnailCache.clear()
    }
    setTimeout(renderThumbnails, 100)
  },
)

onMounted(() => {
  if (props.document) {
    setTimeout(renderThumbnails, 100)
  }
})
</script>

<style lang="scss" scoped>
@use '@/styles/design-tokens' as *;

.pdf-thumbnail-sidebar {
  width: 200px;
  background: rgb(var(--v-theme-surface));
  border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__header {
    padding: $space-md $space-lg;
    font-size: $text-base;
    font-weight: $font-medium;
    border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  }

  &__list {
    flex: 1;
    overflow-y: auto;
    padding: $space-md;
    display: flex;
    flex-direction: column;
    gap: $space-md;
  }

  &__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $space-xs;
    cursor: pointer;
    padding: $space-sm;
    border-radius: $radius-sm;
    border-left: 3px solid transparent;
    transition: background-color $duration-fast $ease-default, border-left-color $duration-fast $ease-default;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.05);
    }

    &--active {
      background: rgba(var(--v-theme-primary), 0.1);
      border-left-color: rgb(var(--v-theme-primary));

      .pdf-thumbnail-sidebar__page-number {
        color: rgb(var(--v-theme-primary));
        font-weight: $font-medium;
      }
    }
  }

  &__canvas {
    max-width: 100%;
    box-shadow: $shadow-xs;
    border-radius: $radius-sm;
    background: white;
  }

  &__page-number {
    font-size: $text-sm;
    color: rgba(var(--v-theme-on-surface), $opacity-secondary);
  }

  @include reduced-motion {
    .pdf-thumbnail-sidebar__item {
      transition: none;
    }
  }
}
</style>
